import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useNavigate } from "react-router-dom";
import * as NavIcons from '../../components/icons/NavIcons';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

export default function IconsPreviewPage() {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [color, setColor] = useState('rgb(40, 148, 216)');
  const [hexColor, setHexColor] = useState('#2894D8');

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        } else {
          setCurrentUser(user);
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
      } finally {
        setAuthChecked(true);
      }
    };
    
    getUser();
  }, []);

  // Convertir hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})` :
      'rgb(40, 148, 216)';
  };

  // Mettre à jour la couleur
  const handleColorChange = (e) => {
    const newHex = e.target.value;
    setHexColor(newHex);
    setColor(hexToRgb(newHex));
  };

  // Liste des icônes à afficher
  const icons = [
    { name: 'ListIcon', Component: NavIcons.ListIcon, label: t('navigation.list') },
    { name: 'EventIcon', Component: NavIcons.EventIcon, label: t('navigation.createEvent') },
    { name: 'MapIcon', Component: NavIcons.MapIcon, label: t('navigation.map') },
    { name: 'MyEventsIcon', Component: NavIcons.MyEventsIcon, label: t('navigation.myEvents') },
    { name: 'SettingsIcon', Component: NavIcons.SettingsIcon, label: t('navigation.settings') },
    { name: 'CoinIcon', Component: NavIcons.CoinIcon, label: t('credits.credits') },
    { name: 'ArrowRightIcon', Component: NavIcons.ArrowRightIcon, label: 'Arrow Right' },
    { name: 'ArrowLeftIcon', Component: NavIcons.ArrowLeftIcon, label: 'Arrow Left' },
    { name: 'ReloadIcon', Component: NavIcons.ReloadIcon, label: 'Reload' },
    { name: 'HeartIcon', Component: NavIcons.HeartIcon, label: 'Heart' },
    { name: 'BuildingIcon', Component: NavIcons.BuildingIcon, label: 'Building' },
    { name: 'RainIcon', Component: NavIcons.RainIcon, label: 'Rain' },
    { name: 'LocationPinIcon', Component: NavIcons.LocationPinIcon, label: 'Location Pin' },
    { name: 'CultureIcon', Component: NavIcons.CultureIcon, label: t('categories.culture') },
    { name: 'ArtIcon', Component: NavIcons.ArtIcon, label: t('categories.art') },
    { name: 'MusicIcon', Component: NavIcons.MusicIcon, label: t('categories.music') },
    { name: 'TheaterIcon', Component: NavIcons.TheaterIcon, label: t('categories.theater') },
    { name: 'CinemaIcon', Component: NavIcons.CinemaIcon, label: t('categories.cinema') },
    { name: 'FestivalIcon', Component: NavIcons.FestivalIcon, label: t('categories.festival') },
    { name: 'SportIcon', Component: NavIcons.SportIcon, label: t('categories.sport') },
    { name: 'WorkshopIcon', Component: NavIcons.WorkshopIcon, label: t('categories.workshop') },
    { name: 'MarketIcon', Component: NavIcons.MarketIcon, label: t('categories.market') },
    { name: 'ConferenceIcon', Component: NavIcons.ConferenceIcon, label: t('categories.conference') },
    { name: 'NatureIcon', Component: NavIcons.NatureIcon, label: t('categories.nature') },
    { name: 'FamilyIcon', Component: NavIcons.FamilyIcon, label: t('categories.family') },
    { name: 'LogoIcon', Component: NavIcons.LogoIcon, label: 'Logo Maploop' },
  ];

  if (adminLoading || !authChecked) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('settings.admin.iconsPreview.title')} backPath="/settings" />

      <div className="p-4">
        {!currentUser ? (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
            <p className="text-amber-700">{t('common.pleaseLogin')}</p>
            <button 
              className="mt-2 bg-[#2894D8] text-white px-4 py-2 rounded hover:bg-[#136E9D] transition-colors"
              onClick={() => window.location.href = '/login'}
            >
              {t('common.login')}
            </button>
          </div>
        ) : (
          <>
            {/* Logo Maploop en grand */}
            <div className="bg-white p-8 rounded-lg shadow-sm mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">Logo Maploop</h2>
              <div className="flex justify-center mb-8">
                <NavIcons.LogoIcon showTitle={true} scale={1} />
              </div>
              
              {/* Palette de couleurs Maploop */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Palette de couleurs Maploop</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Bleu 1 */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-full h-24 rounded-lg shadow-md mb-3"
                      style={{ backgroundColor: 'rgb(40, 148, 216)' }}
                    ></div>
                    <p className="font-semibold text-gray-700">Bleu 1 (Principal)</p>
                    <p className="text-sm text-gray-600 font-mono">rgb(40, 148, 216)</p>
                    <p className="text-sm text-gray-600 font-mono">#2894D8</p>
                  </div>
                  
                  {/* Bleu 3 */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-full h-24 rounded-lg shadow-md mb-3"
                      style={{ backgroundColor: 'rgb(19, 110, 157)' }}
                    ></div>
                    <p className="font-semibold text-gray-700">Bleu 3 (Moyen)</p>
                    <p className="text-sm text-gray-600 font-mono">rgb(19, 110, 157)</p>
                    <p className="text-sm text-gray-600 font-mono">#136E9D</p>
                  </div>
                  
                  {/* Bleu 2 */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-full h-24 rounded-lg shadow-md mb-3"
                      style={{ backgroundColor: 'rgb(7, 78, 108)' }}
                    ></div>
                    <p className="font-semibold text-gray-700">Bleu 2 (Foncé)</p>
                    <p className="text-sm text-gray-600 font-mono">rgb(7, 78, 108)</p>
                    <p className="text-sm text-gray-600 font-mono">#074E6C</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sélecteur de couleur */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <label className="block text-sm font-medium mb-2">
                {t('settings.admin.iconsPreview.colorPicker')}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={hexColor}
                  onChange={handleColorChange}
                  className="w-16 h-10 rounded cursor-pointer"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={hexColor}
                    onChange={handleColorChange}
                    className="w-full p-2 border rounded-lg font-mono text-sm"
                    placeholder="#2894D8"
                  />
                  <p className="text-xs text-gray-500 mt-1">{color}</p>
                </div>
              </div>
            </div>

            {/* Grille d'icônes */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {icons.map(({ name, Component, label }) => (
                <div
                  key={name}
                  className="bg-white p-4 rounded-lg shadow-sm flex flex-col items-center justify-center"
                >
                  <div style={{ color }}>
                    <Component 
                      className="w-12 h-12 mb-2"
                    />
                  </div>
                  <p className="text-xs text-center font-medium text-gray-700 mb-1">
                    {label}
                  </p>
                  <p className="text-xs text-center text-gray-500 font-mono">
                    {name}
                  </p>
                </div>
              ))}
            </div>

            {/* Informations supplémentaires */}
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-800">
                <strong>{t('settings.admin.iconsPreview.info')}</strong>
              </p>
              <p className="text-xs text-blue-700 mt-2">
                {t('settings.admin.iconsPreview.infoDetails')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
